<script setup lang="ts">
/**
 * CasesView — 1:1 порт docs/mockups/public-cases.html (inbox-165).
 *
 * Секции:
 *   1. Hero — badge + gradient title «Как используют link-platform» + sub
 *   2. Industry filter pills (Все / E-commerce / Агентство / Медиа / SaaS /
 *      EdTech / FinTech) — client-side фильтрация
 *   3. Case grid (6 cards): logo + industry + name + 3-metric grid + story-preview
 *   4. Final-CTA card с «Попробовать бесплатно»
 *
 * Spec: docs/specs/2026-05-19-public-behavior.md §32.
 * Static catalog (6 cases — реальные client stories).
 */

import { computed, ref } from 'vue';
import AppPublic from '@/layouts/AppPublic.vue';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

usePageHead({
  title: 'Кейсы link-platform — реальные истории клиентов',
  description:
    'Как E-commerce, агентства, медиа и SaaS используют link-platform: +47% CR, −68% фрод, 2.8М кликов/мес.',
  path: '/cases',
  type: 'website',
  jsonLd: productJsonLd(
    'Кейсы — link-platform',
    '6 кейсов из E-commerce, агентства, медиа, SaaS, EdTech, FinTech.',
  ),
});

interface Metric {
  v: string;
  l: string;
  neutral?: boolean;
}

interface Case {
  slug: string;
  industry: string;
  name: string;
  logoBg: string;
  logoFg?: string;
  logoText: string;
  metrics: [Metric, Metric, Metric];
  story: string;
}

const CASES: Case[] = [
  {
    slug: 'ozonstyle',
    industry: 'E-commerce',
    name: 'OzonStyle',
    logoBg: '#ff5722',
    logoText: 'ОZ',
    metrics: [
      { v: '+47%', l: 'CR' },
      { v: '2.8М', l: 'кликов/мес', neutral: true },
      { v: '−68%', l: 'фрод' },
    ],
    story:
      'Антифрод-ML отсёк 68% бот-трафика на ретаргетинге, освободил бюджет под чистую аудиторию — конверсия из клика в заказ выросла на 47% за квартал.',
  },
  {
    slug: 'alfa-performance',
    industry: 'Агентство',
    name: 'Alfa Performance',
    logoBg: '#4f8cff',
    logoText: 'АЛ',
    metrics: [
      { v: '24', l: 'бренда', neutral: true },
      { v: '+3.2ч', l: 'экономии в день' },
      { v: '+18%', l: 'retention' },
    ],
    story:
      'Бренд-аккаунт + воркспейсы дали 24 брендам собственные дашборды без пересборки тарифа. Команда экономит 3+ часа в день на ручной отчётности.',
  },
  {
    slug: 'rbk-trends',
    industry: 'Медиа',
    name: 'РБК Trends',
    logoBg: '#7c5cff',
    logoText: 'RB',
    metrics: [
      { v: '+212%', l: 'сканов' },
      { v: '12К', l: 'QR на печать', neutral: true },
      { v: '0', l: '«мёртвых» кодов' },
    ],
    story:
      'Динамические QR — печатают офлайн, контент за ссылкой меняется в режиме реального времени. Ни одного «мёртвого» кода после ухода спонсора.',
  },
  {
    slug: 'skillfactory',
    industry: 'SaaS',
    name: 'SkillFactory',
    logoBg: '#3ecf8e',
    logoText: 'SF',
    metrics: [
      { v: '−180К', l: '₽/мес' },
      { v: '42', l: 'кампаний/мес', neutral: true },
      { v: '+27%', l: 'ROI' },
    ],
    story:
      'Заменили Bitly Enterprise на link-platform — экономия 180К₽/мес. AI-копайлот заранее находит «протухающие» кампании, ROI вырос на 27%.',
  },
  {
    slug: 'tinkoff-uchyoba',
    industry: 'EdTech',
    name: 'Тинькофф Учеба',
    logoBg: '#f5a524',
    logoText: 'TS',
    metrics: [
      { v: '−31%', l: 'CPL' },
      { v: '+19%', l: 'конверсия' },
      { v: '3-way', l: 'тесты', neutral: true },
    ],
    story:
      'A/B/C-тесты на одной короткой ссылке: 3 варианта лендинга, авто-promote победителя через 14 дней. CPL упал на 31% после первого цикла.',
  },
  {
    slug: 'kupi-investiruy',
    industry: 'FinTech',
    name: 'КупиИнвестируй',
    logoBg: '#2fc6f6',
    logoText: 'КИ',
    metrics: [
      { v: '100%', l: 'compliance', neutral: true },
      { v: '0', l: 'инцидентов' },
      { v: '18мес', l: 'audit лог', neutral: true },
    ],
    story:
      'Все клики и редиректы — в audit-логе 18 месяцев. Регуляторские проверки проходят за минуты вместо дней. Ни одного инцидента с FATCA/MiFID за 2 года.',
  },
];

const INDUSTRIES = ['Все', 'E-commerce', 'Агентство', 'Медиа', 'SaaS', 'EdTech', 'FinTech'] as const;
type Industry = (typeof INDUSTRIES)[number];

const activeIndustry = ref<Industry>('Все');

const filteredCases = computed<Case[]>(() => {
  if (activeIndustry.value === 'Все') return CASES;
  return CASES.filter((c) => c.industry === activeIndustry.value);
});
</script>

<template>
  <AppPublic>
    <!-- ── Hero ── -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">600+ компаний используют · 6 кейсов в библиотеке</div>
          <h1 class="hero-title">
            Как используют <span class="gradient">link-platform</span>
          </h1>
          <p class="hero-sub">
            Реальные результаты команд из E-commerce, агентств, медиа, SaaS и
            финтеха. Метрики из dashboard'ов клиентов с их разрешения.
          </p>
          <div class="industry-filters">
            <button
              v-for="ind in INDUSTRIES"
              :key="ind"
              type="button"
              :class="['chip', { active: activeIndustry === ind }]"
              @click="activeIndustry = ind"
            >{{ ind }}</button>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Case grid ── -->
    <section class="cases">
      <div class="container">
        <div class="case-grid">
          <article
            v-for="c in filteredCases"
            :key="c.slug"
            class="case-card"
            role="link"
            tabindex="0"
          >
            <header class="case-head">
              <div class="case-logo" :style="{ background: c.logoBg, color: c.logoFg || '#fff' }">{{ c.logoText }}</div>
              <div>
                <div class="case-industry">{{ c.industry }}</div>
                <div class="case-name">{{ c.name }}</div>
              </div>
            </header>
            <div class="case-metrics">
              <div v-for="(m, i) in c.metrics" :key="i" class="case-metric">
                <div :class="['v', { neutral: m.neutral }]">{{ m.v }}</div>
                <div class="l">{{ m.l }}</div>
              </div>
            </div>
            <p class="case-story">{{ c.story }}</p>
            <a class="case-cta" :href="`/cases/${c.slug}`">Читать целиком →</a>
          </article>
        </div>
        <div v-if="filteredCases.length === 0" class="empty">
          В индустрии «{{ activeIndustry }}» пока нет опубликованных кейсов. Скоро появятся.
        </div>
      </div>
    </section>

    <!-- ── Final CTA ── -->
    <section class="final-cta-section">
      <div class="container">
        <div class="final-cta">
          <h3>Готовы попробовать?</h3>
          <p>
            Бесплатный тариф на 30 дней — без карты. Импорт ссылок из CSV
            за минуту, native-интеграции с CRM и рекламой.
          </p>
          <div class="actions">
            <a class="btn btn-primary" href="/signup">Попробовать бесплатно</a>
            <a class="btn btn-outline" href="/pricing">Тарифы</a>
          </div>
        </div>
      </div>
    </section>
  </AppPublic>
</template>

<style scoped>
.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}

/* ── Hero ── */
.hero {
  position: relative;
  padding: 88px 0 56px;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 30% 20%, rgba(79, 140, 255, 0.15), transparent 70%),
    radial-gradient(ellipse 50% 40% at 80% 30%, rgba(124, 92, 255, 0.10), transparent 70%);
  pointer-events: none;
}
.hero-content {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  margin-bottom: 24px;
  background: var(--accent-soft);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 13px;
  color: var(--accent-hi);
  font-weight: 500;
}
.hero-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 8px var(--accent);
}
.hero-title {
  font-size: 56px;
  line-height: 1.08;
  letter-spacing: -0.025em;
  font-weight: 700;
  margin-bottom: 24px;
  color: var(--fg);
}
.hero-title .gradient {
  background: var(--gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.hero-sub {
  font-size: 19px;
  line-height: 1.55;
  color: var(--fg-muted);
  max-width: 640px;
  margin: 0 auto 32px;
}

.industry-filters {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}
.industry-filters .chip {
  padding: 7px 14px;
  font-size: 13.5px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 100px;
  color: var(--fg-muted);
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.industry-filters .chip:hover { color: var(--fg); }
.industry-filters .chip.active {
  border-color: var(--accent);
  color: var(--accent-hi);
  background: var(--accent-soft);
}

/* ── Cases ── */
.cases { padding: 64px 0; }
.case-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}
.case-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.15s;
}
.case-card:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
}
.case-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.case-logo {
  width: 44px;
  height: 44px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 14px;
  flex-shrink: 0;
}
.case-industry {
  font-size: 11.5px;
  color: var(--fg-dim);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.case-name {
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
  color: var(--fg);
}

.case-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
  margin-bottom: 16px;
}
.case-metric {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.case-metric .v {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  color: var(--success);
}
.case-metric .v.neutral { color: var(--fg); }
.case-metric .l {
  font-size: 11px;
  color: var(--fg-dim);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.case-story {
  font-size: 13.5px;
  color: var(--fg-muted);
  line-height: 1.55;
  margin-bottom: 18px;
  flex: 1;
}
.case-cta {
  align-self: flex-start;
  color: var(--accent-hi);
  font-size: 13.5px;
  font-weight: 500;
  text-decoration: none;
}
.case-cta:hover { color: var(--accent); }

.empty {
  text-align: center;
  padding: 48px 16px;
  color: var(--fg-muted);
}

/* ── Final CTA ── */
.final-cta-section { padding: 32px 0 96px; }
.final-cta {
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: 16px;
  padding: 48px 56px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.final-cta::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(79, 140, 255, 0.10), transparent 60%);
  pointer-events: none;
}
.final-cta > * { position: relative; }
.final-cta h3 {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 12px;
  color: var(--fg);
}
.final-cta p {
  font-size: 16px;
  color: var(--fg-muted);
  margin-bottom: 26px;
  max-width: 580px;
  margin-left: auto;
  margin-right: auto;
}
.final-cta .actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  text-decoration: none;
  transition: all 0.12s;
}
.btn-primary {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);
}
.btn-primary:hover { background: var(--accent-hi); border-color: var(--accent-hi); }
.btn-outline {
  border-color: var(--border-strong);
  color: var(--fg);
}
.btn-outline:hover { border-color: var(--fg-muted); background: var(--bg-elev); }

@media (max-width: 980px) {
  .container { padding: 0 24px; }
  .hero-title { font-size: 44px; }
  .hero-sub { font-size: 17px; }
  .case-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .container { padding: 0 18px; }
  .hero { padding: 48px 0 40px; }
  .hero-title { font-size: 30px; }
  .hero-sub { font-size: 15.5px; margin-bottom: 24px; }
  .industry-filters .chip { font-size: 12px; padding: 6px 12px; }
  .cases { padding: 56px 0; }
  .case-grid { grid-template-columns: 1fr; }
  .final-cta { padding: 32px 22px; }
  .final-cta h3 { font-size: 22px; }
}
</style>
